import { useCallback, useEffect, useRef, useState } from "react";
import { onlyDigits } from "@/lib/brasil";

type AutomaticLookupOptions<T> = {
  value: string;
  digits: number;
  enabled: boolean;
  lookup: (value: string) => Promise<T>;
  onSuccess: (data: T) => void;
  onError: (error: Error) => void;
  delay?: number;
};

export function useAutomaticBrazilLookup<T>({
  value,
  digits,
  enabled,
  lookup,
  onSuccess,
  onError,
  delay = 450,
}: AutomaticLookupOptions<T>) {
  const [loading, setLoading] = useState(false);
  const lastLookupRef = useRef("");
  const requestRef = useRef(0);
  const successRef = useRef(onSuccess);
  const errorRef = useRef(onError);

  successRef.current = onSuccess;
  errorRef.current = onError;

  const run = useCallback(async (candidate = value, force = true) => {
    const normalized = onlyDigits(candidate);
    if (normalized.length !== digits) return false;
    if (!force && lastLookupRef.current === normalized) return false;

    lastLookupRef.current = normalized;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const data = await lookup(normalized);
      if (requestId === requestRef.current) successRef.current(data);
      return true;
    } catch (cause) {
      if (requestId === requestRef.current) {
        errorRef.current(cause instanceof Error ? cause : new Error("Consulta indisponivel"));
      }
      return false;
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [digits, lookup, value]);

  useEffect(() => {
    const normalized = onlyDigits(value);
    if (!enabled || normalized.length !== digits || lastLookupRef.current === normalized) return;
    const timer = window.setTimeout(() => void run(normalized, false), delay);
    return () => window.clearTimeout(timer);
  }, [delay, digits, enabled, run, value]);

  return { loading, run };
}
